/**
 * Tempo exibido nos cards de SLA da requisição de compra.
 *
 * O servidor é a fonte da verdade: `GET /api/purchases/{id}/times` recalcula tudo a partir dos
 * logs de mudança de status. O cliente apenas anima os segundos entre duas respostas, e por isso
 * o overlay é medido pelo relógio (agora menos o instante da resposta) em vez de um contador
 * incremental — um contador atrasa quando o navegador limita o `setInterval` da aba em segundo
 * plano e volta a zero a cada remontagem.
 *
 * A regra de negócio do tempo não muda: só conta trabalho, pausado em aprovação e reenvio.
 */

export interface PurchaseTimeEntryLike {
  step_name?: string;
  started_at?: string;
  ended_at?: string;
  time_spent_seconds: number;
  is_active: boolean;
}

export interface PurchaseTimesLike {
  total_time_seconds: number;
  time_in_steps?: PurchaseTimeEntryLike[];
  time_in_groups?: PurchaseTimeEntryLike[];
}

export interface PurchaseDisplayTimes {
  /** Falso enquanto o servidor não respondeu: a tela deve mostrar placeholder, não zero. */
  isReady: boolean;
  totalSeconds: number;
  currentStepSeconds: number;
  isTotalRunning: boolean;
  isStepRunning: boolean;
}

/** Mesma normalização do backend (`_normalize_step_key`), tolerante a acentos. */
export function normalizePurchaseStepKey(value?: string | null): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

export function elapsedSecondsSince(anchorMs: number | null | undefined, nowMs: number): number {
  if (!anchorMs) return 0;
  const seconds = Math.floor((nowMs - anchorMs) / 1000);
  return seconds > 0 ? seconds : 0;
}

function hasActiveServerTimer(times: PurchaseTimesLike): boolean {
  const steps = times.time_in_steps ?? [];
  const groups = times.time_in_groups ?? [];
  return steps.some((entry) => entry.is_active) || groups.some((entry) => entry.is_active);
}

function entryOrderValue(entry: PurchaseTimeEntryLike): number {
  return new Date(entry.ended_at || entry.started_at || 0).getTime() || 0;
}

export function resolvePurchaseDisplayTimes(params: {
  times: PurchaseTimesLike | null | undefined;
  currentStep?: string | null;
  isFinal: boolean;
  isPaused: boolean;
  fetchedAtMs: number | null;
  nowMs: number;
}): PurchaseDisplayTimes {
  const { times, currentStep, isFinal, isPaused, fetchedAtMs, nowMs } = params;

  if (!times) {
    return {
      isReady: false,
      totalSeconds: 0,
      currentStepSeconds: 0,
      isTotalRunning: false,
      isStepRunning: false,
    };
  }

  const overlaySeconds = elapsedSecondsSince(fetchedAtMs, nowMs);
  const canRun = !isFinal && !isPaused;

  // Sem timer ativo e sem tempo acumulado, o servidor está dizendo que o relógio não corre.
  // Animar nesse caso inventaria um tempo que ninguém guarda e que reinicia a cada refresh.
  const isTotalRunning =
    canRun && (hasActiveServerTimer(times) || times.total_time_seconds > 0);
  const totalSeconds = times.total_time_seconds + (isTotalRunning ? overlaySeconds : 0);

  const currentStepKey = normalizePurchaseStepKey(currentStep);
  const matchingEntries = currentStepKey
    ? (times.time_in_steps ?? []).filter(
        (entry) => normalizePurchaseStepKey(entry.step_name) === currentStepKey
      )
    : [];
  const activeEntry = matchingEntries.find((entry) => entry.is_active);
  const latestEntry = [...matchingEntries].sort(
    (a, b) => entryOrderValue(b) - entryOrderValue(a)
  )[0];

  const isStepRunning = canRun && !!activeEntry;
  const stepBaseSeconds = activeEntry
    ? activeEntry.time_spent_seconds
    : (latestEntry?.time_spent_seconds ?? 0);

  return {
    isReady: true,
    totalSeconds,
    currentStepSeconds: stepBaseSeconds + (isStepRunning ? overlaySeconds : 0),
    isTotalRunning,
    isStepRunning,
  };
}
